import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { getBuilderModelName } from "@/libs/builder/tools/getBuilderModelName";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const qrAgeVerificationContainerInputs = [
  {
    name: "ignoreUrls",
    type: "list",
    required: true,
    subFields: [
      {
        name: "url",
        type: "url",
        required: true,
      },
    ],
    defaultValue: [
      {
        url: "/login",
      },
    ],
  },
  {
    name: "logo",
    type: "file",
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F9d1d2ffe26704533a8ad5fbf1abac751",
    required: true,
  },
  {
    name: "logoAlt",
    type: "string",
    defaultValue: "Logo",
  },
  {
    name: "ageVerificationMethod",
    type: "string",
    friendlyName: "Age Verification Method - (DIIA & PASS)",
    enum: ["DIIA_ASYNC", "PASS"],
    defaultValue: "DIIA_ASYNC",
  },
  {
    name: "title",
    friendlyName: "Title - (DIIA & PASS)",
    type: "string",
    required: true,
    defaultValue: "Age Verification",
  },
  {
    name: "ttlOfCookie",
    type: "string",
    friendlyName: "Time to live of age verification cookie in seconds - (DIIA & PASS)",
    defaultValue: "86400",
  },
  {
    name: "description",
    friendlyName: "Verification description - (DIIA & PASS)",
    required: true,
    type: "richText",
    defaultValue:
      "Ploom.co.kr is only available to adult smokers aged 19 or older. You must sign up or log in to access the entire website content and order online. Sign up and experience various benefits such as free shipping and exclusive events.",
  },
  {
    name: "continueButtonLabel",
    friendlyName: "Continue with verification button Label - (DIIA & PASS)",
    type: "string",
    required: true,
    defaultValue: "Continue",
  },
  {
    name: "exitButtonLabel",
    friendlyName: "Exit button label (for DIIA) / Continue as a guest button label (for PASS)",
    required: true,
    type: "string",
    defaultValue: "Close",
  },
  {
    name: "signInButtonLabel",
    type: "string",
    friendlyName: "Button Label for Sign In (PASS only)",
    defaultValue: "Sign In",
  },
  {
    name: "tryAgainButtonLabel",
    friendlyName: "Button Label for Try Again (PASS only)",
    type: "string",
    defaultValue: "Try Again",
  },
  {
    name: "backButtonLabel",
    type: "string",
    friendlyName: "Back button label - (DIIA & PASS)",
    required: true,
    defaultValue: "Back",
  },
  {
    name: "exitButtonLink",
    type: "url",
    friendlyName: "Exit button link (DIIA only)",
    required: true,
    defaultValue: "/",
  },
  {
    name: "legalNotice",
    friendlyName: "Legal notice (DIIA & PASS)",
    type: "richText",
    required: true,
    defaultValue: "Copyright © 2025 Ploom. All rights reserved.",
  },
  {
    name: "desktopImageSrc",
    friendlyName: "Desktop image (DIIA & PASS)",
    type: "file",
    required: true,
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F94ec7d536d984e4baef672ff5f09c211",
  },
  {
    name: "tabletImageSrc",
    friendlyName: "Tablet image (DIIA & PASS)",
    type: "file",
  },
  {
    name: "mobileImageSrc",
    friendlyName: "Mobile image (DIIA & PASS)",
    type: "file",
  },
  {
    name: "alt",
    friendlyName: "Alternative Text",
    type: "string",
    required: true,
    defaultValue: "Rich experience",
  },
  {
    name: "verificationTitle",
    friendlyName: "Verification step title (DIAA & PASS)",
    required: true,
    type: "string",
    defaultValue: "Authenticate with Provider",
  },
  {
    name: "mobileQrHelper",
    friendlyName: "QR code hepler only for mobile (DIIA only)",
    type: "string",
  },
  {
    name: "verificationSteps",
    friendlyName: "Verification steps (DIIA & PASS)",
    type: "richText",
    required: true,
    defaultValue: "Please complete identity verification in the pop-up window.",
  },
  {
    name: "getNewQrButtonLabel",
    friendlyName: "Get new QR button label (DIIA only)",
    type: "string",
    required: true,
    defaultValue: "Create New QR",
  },
  {
    name: "qrStaleError",
    friendlyName: "QR error message for stale state (DIIA only)",
    required: true,
    type: "string",
    defaultValue: "QR code has expired",
  },
  {
    name: "qrTtl",
    friendlyName: "Time to live of qr code in seconds (DIIA only)",
    type: "string",
    defaultValue: "180",
  },
  {
    name: "qrCommonError",
    friendlyName: "Common error message when BE doesn't return common error (DIIA & PASS)",
    required: true,
    type: "string",
    defaultValue: "An error occurred",
  },
  {
    name: "failedProviderVerificationError",
    friendlyName: "Error message when provider failed validation (DIIA & PASS)",
    required: true,
    type: "string",
    defaultValue: "Verification failed",
  },
  {
    name: "signInURL",
    friendlyName: "Sign in URL (PASS only)",
    type: "url",
    defaultValue: "/",
  },
  {
    name: "waitingMessage",
    friendlyName: "Waiting for verification message for PASS Age verification in progress (PASS only)",
    type: "richText",
    defaultValue:
      "Adult Verification Guide Adult verification is required to use the site. Personal information collected during the verification process will not be used for any other purpose. For more information, please refer to our [Privacy Policy]. Adult verification through PASS is completed within a few minutes.",
  },
  {
    name: "helperText",
    friendlyName: "Helper Text for PASS Age verification in progress - shown in the place of description (PASS only)",
    type: "string",
    defaultValue: "Please proceed with identity verification.",
  },
  {
    name: "backgroundColor",
    friendlyName: "Background Color (RGBA)",
    type: "color",
    defaultValue: "rgba(0, 0, 0, 0.9)",
  },
  {
    name: "backgroundBlur",
    type: "number",
    defaultValue: 0,
  },
  {
    name: "verificationErrorHelperText",
    type: "string",
    friendlyName: "Text for verification error message if Pass verification fails (PASS only)",
    defaultValue: "",
  },
  {
    name: "missingDataFromAvError",
    friendlyName: "Text for error message if phoneNumber is not present in payload after AV (PASS only)",
    type: "string",
    defaultValue: "",
  },
] as const satisfies Inputs;

export const QrAgeVerificationContainerComponent: RegisteredComponent = {
  name: "QrAgeVerificationContainer",
  component: dynamic(async () =>
    withValidation(
      qrAgeVerificationContainerInputs,
      (await import("./QrAgeVerificationContainer")).QrAgeVerificationContainer,
      "QrAgeVerificationContainer"
    )
  ),
  models: [getBuilderModelName("qr-age-verification-popup")],
  inputs: qrAgeVerificationContainerInputs,
  canHaveChildren: true,
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
    builderComponents: true,
    builderContext: true,
  },
  defaultChildren: [],
  childRequirements: {
    message: "You can only put HealthBanner component inside this component",
    query: {
      "component.name": {
        $in: ["HealthBanner"],
      },
    },
  },
};
